import { removeStartEndDoubleQuotes } from '@/utils'
import { MainFooter, MainHeader } from '@mercedes-benz/oneweb-next-react-components'
import { ComponentProps } from 'react'
import config from '@/config'

export const getGlobalFooterData = (): Promise<ComponentProps<typeof MainFooter>> => {
  return fetch(
    config.CMS_HOST +
    '/api/ow-global-footer?populate=GlobalFooterRow1CTAs&populate=GlobalFooterRow2CTAs&populate=Touchpoint&populate=Touchpoint.icon&populate=Touchpoint.popup',
    {
      headers: {
        Authorization: `${config.CMS_AUTHORIZATION}`,
      },
    }
  ).then((res) => res.json())
}

export const getGlobalHeaderData = (): Promise<ComponentProps<typeof MainHeader>['burgerMenuData']> => {
  return fetch(
    config.CMS_HOST +
    '/api/ow-global-header?populate=FirstLevelMenu&populate=FirstLevelMenu.Level2Menu&populate=FirstLevelMenu.searchPlaceholder',
    {
      headers: {
        Authorization: `${config.CMS_AUTHORIZATION}`,
      },
    }
  ).then((res) => res.json())
}

export const getGlobalVehicleListData = (preview: boolean = false): Promise<ComponentProps<typeof MainHeader>['vehicleListData']> => {
  return fetch(
    config.CMS_HOST +
    `/api/ow-vehicle-list?${preview ? 'publicationState=preview&' : ''}
  populate=OWVehicleBody
  &populate=OWVehicleBody.OWVehicleClass
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.detailLink
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.detailLink.link
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.CTAs
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.CTAs.link
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.CTAs.link.link
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.nameImageMobile
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.nameImagePC
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.vehicleData.model
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.vehicleData.vehicleImagePC
  &populate=OWVehicleBody.OWVehicleClass.OWVehicleModel.vehicleDetail.vehicleData.vehicleImageMobile
`,
    {
      headers: {
        Authorization: `${config.CMS_AUTHORIZATION}`,
      },
    }
  ).then((res) => res.json())
}

/**
 * @returns 0: valid token-suspended, 1: invalid token-active,  2: no token - notLogin
 */
export const verifyToken = () => {
  const token = removeStartEndDoubleQuotes(localStorage.getItem('MME-TOKEN')).replace('bearer ', '')
  if (!token) {
    return Promise.resolve(2)
  }
  const url = config.ONE_WEB_HOST + '/ow-cdm/token/verification'
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ mmeToken: token }),
  })
    .then((res) => res.json())
    .then((res) => {
      const isValid = res?.code === 0
      if (!isValid && document) {
        localStorage.removeItem('realName')
      }
      return +isValid
    })
}

export const getAccountInfo = () => {
  const mobile = removeStartEndDoubleQuotes(localStorage.getItem('mobile'))
  const userId = localStorage.getItem('USER_ID')
  const token = localStorage.getItem('MME-TOKEN')
  return fetch(`${config.ONE_WEB_HOST}/ow-cdm/accounts?phoneNumber=${mobile}`, {
    headers: {
      cid: removeStartEndDoubleQuotes(userId),
      'mme-token': removeStartEndDoubleQuotes(token).replace('bearer ', ''),
      phone: mobile,
    },
  })
    .then((res) => res.json())
    .then((res) => {
      if (res?.result?.name) {
        localStorage.setItem('realName', res.result.name)
      }
    })
}

export const getCarComparisonStrapiData = async () => {
  const data = await fetch(
    config.CMS_HOST + `/api/ow-car-comparison-and-engine-detail?populate=*`,
    {
      headers: {
        Authorization: `${config.CMS_AUTHORIZATION}`,
      },
    }
  )
  const content = await data.json()
  return {
    data: content, success: true
  }
}

export const getBodyStyle = async (preview: boolean = false, nstCodeList?: string[]) => {
  const data = await fetch(
    `/api/graphql/bodystyle?` + new URLSearchParams({ preview: preview.toString(), nstCodeList: nstCodeList?.join(',') || '' })
  )
  const content = await data.json()
  return content
}

export const getEngine = async (preview: boolean, nstCodeList: string[]) => {
  const data = await fetch(
    `/api/graphql/engine?` + new URLSearchParams({ preview: preview.toString(), nstCodeList: nstCodeList?.join(',') || '' })
  )
  const content = await data.json()
  return content
}

export const getGraphqlDefaultQuery = () => {
  return `
query BodyStyles ($preview: Boolean, $nstCodeList: [String]){
    bodyStyles (preview: $preview, nstCodeList: $nstCodeList){
        cnName
        enName
        status
        classes {
            cnName
            enName
            status
            desktopImage {
                name
                thumbnailUrl
                url
            }
            mobileImage {
                name
                thumbnailUrl
                url
            }
            amgDesktopImage {
                name
                thumbnailUrl
                url
            }
            amgMobileImage {
                name
                thumbnailUrl
                url
            }
            maybachDesktopImage {
                name
                thumbnailUrl
                url
            }
            maybachMobileImage {
                name
                thumbnailUrl
                url
            }
            gDesktopImage {
                name
                thumbnailUrl
                url
            }
            gMobileImage {
                name
                thumbnailUrl
                url
            }
            models {
                cnName
                enName
                brand
                draftNst
                publishedNst
                draftMsrp
                publishedMsrp
                hideInFc
                classId
                status
                allVehiclePageImageMobile {
                    name
                    thumbnailUrl
                    url
                }
                allVehiclePageImageDesktop {
                    name
                    thumbnailUrl
                    url
                }
                usedVehicleAdvertisingPageImage {
                    name
                    thumbnailUrl
                    url
                }
                brochurePdfDesktop {
                    name
                    thumbnailUrl
                    url
                }
                brochurePdfMobile {
                    name
                    thumbnailUrl
                    url
                }
                engines {
                    vdmEngineBaseInfo {
                        cnName
                        enName
                        status
                        msrp
                        hideInFc
                        hideInEngineDetailPage
                        phev
                        year
                        modelYear
                        changeYear
                        salesModelYear
                        typeClass
                        nstCode
                        nst
                        bm6
                        bodyStyle
                        isTEV
                        isMoD
                        bauMuster
                        brandNameCn
                        brandNameEn
                        series
                        seriesCn
                        typeClassNameEn
                        typeClassNameCn
                        allDetailSpecEngineVehicleImageDesktop {
                            name
                            thumbnailUrl
                            url
                        }
                        allDetailSpecEngineVehicleImageMobile {
                            name
                            thumbnailUrl
                            url
                        }
                    }
                }
            }
        }
    }
}

`
}

export const getGlobalBodyStyle = (preview: boolean = false, nstCodeList: string[] | string = '', query: string = ''): Promise<any> => {
  const url = config.DOTS_API_URL + `/vehicle-service/api/graphql`

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      query: query || getGraphqlDefaultQuery(),
      variables: {
        preview: preview || false,
        nstCodeList: nstCodeList || ''
      }
    })
  }).then((res) => res.json())
}


export const getClassInstance = async (nstCode: string) => {
  const url = config.E_STORE_HOST + '/api/ecommerce/customers/nsts/' + nstCode

  const options = {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    }
  }

  try {
    const response = await fetch(url, options)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    const data = await response.json()
    if (!data.data || !data.data.classCode) {
      throw new Error('No classCode found')
    }
    return data.data
  } catch (error) {
    console.error('Error fetching customer data:', error)
  }
}

export const getVehicleCertificationReport = (vin: string): Promise<any> => {
  // const url = 'https://api.oneweb-stage.mercedes-benz.com.cn' + '/oneweb-usedcar-bff/ow-vehicle/vehicles/report/certificated?vin=' + vin
  const url = config.ONE_WEB_HOST + '/oneweb-usedcar-bff/ow-vehicle/vehicles/report/certificated?vin=' + vin
  return fetch(url, {
    headers: {
      'Content-Type': 'application/json',
    },
  }).then((res) => res.json()).then(res => res)
}

export const verifyVehicleEnvironmentalInfo = async (data: { validate: string, vinVal: string }): Promise<any> => {
  const url = `${config.E_STORE_HOST}/environmental-service/environment/v1/search`

  const res = await fetch(url, {
    method: "POST",
    headers: {
      "X-api-key": config.X_API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  })
  return await res.json()
}
